import { get, del } from './client'
import type { LogEntry } from '@/types'

export interface LogListParams {
  page?: number
  page_size?: number
  type?: number
  start_timestamp?: number
  end_timestamp?: number
  username?: string
  token_name?: string
  model_name?: string
  channel?: number
  group?: string
  request_id?: string
  upstream_request_id?: string
  user_id?: number
  status?: string
  error_stage?: string
  stream?: boolean | string
  min_total_ms?: number
  min_upstream_ms?: number
  min_ttft_ms?: number
  ip?: string
  token_id?: number
}

export interface LogListResponse {
  items: LogEntry[]
  total: number
  page: number
  page_size: number
}

/** new-api 返回 `snake_case`（`model.Log` 的 `json` 标签），与 `LogEntry` 对齐 */
function normalizeLogEntry(row: unknown): LogEntry {
  const r = row as Record<string, unknown>
  const isStream = (r.is_stream ?? r.isStream) as boolean | undefined
  return {
    id: Number(r.id ?? 0),
    userId: Number(r.user_id ?? r.userId ?? 0),
    username: (r.username as string) || undefined,
    type: Number(r.type ?? 0),
    content: String(r.content ?? ''),
    modelName: String(r.model_name ?? r.modelName ?? ''),
    tokenName: (r.token_name ?? r.tokenName) as string | undefined,
    tokenId: numOrUndef(r.token_id ?? r.tokenId),
    channel: numOrUndef(r.channel),
    channelName: (r.channel_name ?? r.channelName) as string | undefined,
    group: (r.group as string) || undefined,
    quota: Number(r.quota ?? 0),
    promptTokens: Number(r.prompt_tokens ?? r.promptTokens ?? 0),
    completionTokens: Number(r.completion_tokens ?? r.completionTokens ?? 0),
    useTime: numOrUndef(r.use_time ?? r.useTime),
    isStream,
    stream: (r.stream as boolean | undefined) ?? isStream,
    ip: (r.ip as string) || undefined,
    clientIp: (r.client_ip ?? r.clientIp ?? r.ip) as string | undefined,
    requestId: (r.request_id ?? r.requestId) as string | undefined,
    requestAt: numOrUndef(r.request_at ?? r.requestAt),
    status: (r.status as string) || undefined,
    errorStage: (r.error_stage ?? r.errorStage) as string | undefined,
    errorCode: (r.error_code ?? r.errorCode) as string | undefined,
    upstreamStatusCode: numOrUndef(r.upstream_status_code ?? r.upstreamStatusCode),
    upstreamRequestId: (r.upstream_request_id ?? r.upstreamRequestId) as string | undefined,
    totalMs: numOrUndef(r.total_ms ?? r.totalMs),
    upstreamTtfbMs: numOrUndef(r.upstream_ttfb_ms ?? r.upstreamTtfbMs),
    upstreamTotalMs: numOrUndef(r.upstream_total_ms ?? r.upstreamTotalMs),
    clientWriteMs: numOrUndef(r.client_write_ms ?? r.clientWriteMs),
    clientTtftMs: numOrUndef(r.client_ttft_ms ?? r.clientTtftMs),
    retryCount: numOrUndef(r.retry_count ?? r.retryCount),
    userAgent: (r.user_agent ?? r.userAgent) as string | undefined,
    otherInfo: (r.other ?? r.otherInfo) as string | undefined,
    createdAt: Number(r.created_at ?? r.createdAt ?? 0),
  }
}

function numOrUndef(v: unknown): number | undefined {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function normalizeLogListResponse(data: LogListResponse): LogListResponse {
  return {
    ...data,
    items: (data?.items ?? []).map((x) => normalizeLogEntry(x)),
  }
}

export type LogStatParams = Omit<LogListParams, 'page' | 'page_size'>

export interface LogStatResult {
  quota: number
  token_count?: number
  rpm: number
  tpm: number
  total_requests: number
  success_count: number
  error_count: number
  success_rate: number
  quota_sum: number
  prompt_tokens: number
  completion_tokens: number
  cache_tokens: number
  avg_total_ms: number
  p95_total_ms: number
  avg_upstream_ms: number
  p95_upstream_ms: number
  avg_ttft_ms: number
}

function normalizeLogStatResult(data: Record<string, unknown>): LogStatResult {
  const prompt = Number(data.prompt_tokens ?? 0)
  const completion = Number(data.completion_tokens ?? 0)
  return {
    quota: Number(data.quota ?? data.quota_sum ?? 0),
    token_count: prompt + completion || undefined,
    rpm: Number(data.rpm ?? 0),
    tpm: Number(data.tpm ?? 0),
    total_requests: Number(data.total_requests ?? 0),
    success_count: Number(data.success_count ?? 0),
    error_count: Number(data.error_count ?? 0),
    success_rate: Number(data.success_rate ?? 0),
    quota_sum: Number(data.quota_sum ?? data.quota ?? 0),
    prompt_tokens: prompt,
    completion_tokens: completion,
    cache_tokens: Number(data.cache_tokens ?? 0),
    avg_total_ms: Number(data.avg_total_ms ?? 0),
    p95_total_ms: Number(data.p95_total_ms ?? 0),
    avg_upstream_ms: Number(data.avg_upstream_ms ?? 0),
    p95_upstream_ms: Number(data.p95_upstream_ms ?? 0),
    avg_ttft_ms: Number(data.avg_ttft_ms ?? 0),
  }
}

// ==================== 日志查询（用户） ====================

export async function getSelfLogsApi(params?: LogListParams): Promise<LogListResponse> {
  const data = await get<LogListResponse>('/api/log/self', params)
  return normalizeLogListResponse(data)
}

export async function getSelfLogStatsApi(params?: LogStatParams): Promise<LogStatResult> {
  const data = await get<Record<string, unknown>>('/api/log/self/stat', params)
  return normalizeLogStatResult(data)
}

// ==================== 日志查询（管理员） ====================

export async function getAllLogsApi(params?: LogListParams): Promise<LogListResponse> {
  const data = await get<LogListResponse>('/api/log', params)
  return normalizeLogListResponse(data)
}

export async function getLogStatsApi(params?: LogStatParams): Promise<LogStatResult> {
  const data = await get<Record<string, unknown>>('/api/log/stat', params)
  return normalizeLogStatResult(data)
}

export async function deleteOldLogsApi(targetTimestamp: number): Promise<number> {
  return del<number>('/api/log', { params: { target_timestamp: targetTimestamp } })
}

export async function getChannelAffinityCacheApi(): Promise<unknown> {
  return get('/api/log/channel_affinity_usage_cache')
}
